// TODO: Worth refactoring to react-aria?
import { Dialog } from '@headlessui/react';
import clsx, { cx } from 'classix';
import { ReactNode, useRef } from 'react';
import { Icon } from '../atoms';
import { Button, ButtonVariants, ProgressBar } from '../molecules';

export type ModalCallToAction = {
  callback: () => void;
  text: string;
  buttonVariant?: ButtonVariants;
  isDisabled?: () => boolean;
};

export type ModalProps = (
  | {
      headerDivision: 'step';
      progress: number;
    }
  | {
      headerDivision: 'divider';
    }
) & {
  isOpen: boolean;
  onClose?: () => void;
  variant: 'dialog' | 'full-screen';

  title?: string;

  primaryCTA?: ModalCallToAction & {
    // TODO: Should this be a function for parity with isDisabled?
    isLoading?: boolean;
    isLoadingText?: string;
  };
  secondaryCTA?: ModalCallToAction;

  /** Exposes setting the className for the Modal panel div.
   * This is useful for setting the width of the modal, but is an escape hatch to be used
   * sparingly. */
  panelClassName?: string;
  children: ReactNode;
  ignoreExtraPadding?: boolean;
  dismissable?: boolean;
};

export const Modal = (props: ModalProps) => {
  const {
    isOpen,
    ignoreExtraPadding,
    onClose,
    title,
    primaryCTA,
    secondaryCTA,
    panelClassName,
    children,
    variant,
    dismissable = true,
  } = {
    ...props,
  };

  const formRef = useRef<HTMLFormElement>(null);
  const contentRef = useRef(null);

  return (
    <Dialog
      className={'relative z-50 font-sans'}
      open={isOpen}
      // Disables default behavior of closing on outside click or pressing escape key
      onClose={() => {}}
      // Used here to *avoid* setting an initially focused element.
      // HeadlessUI is IMO too prescriptive in that they require some element in the modal to be auto focused upon modal open,
      // even though it's visually jarring: https://github.com/tailwindlabs/headlessui/issues/265.
      // The CTAs can still be focused via the tab key so I do not believe this is a valid concern.
      initialFocus={contentRef}
    >
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      {variant === 'dialog' && <div className='fixed inset-0 bg-black/30' aria-hidden='true' />}
      <form
        ref={formRef}
        action={'javascript:void(0);'}
        className={
          variant === 'dialog' ?
            'overflow-y-scroll md:overflow-y-scroll fixed inset-0 flex w-screen items-center justify-center'
          : 'fixed inset-0 w-screen h-screen z-30'
        }
      >
        {/** Workaround for https://github.com/tailwindlabs/headlessui/discussions/412 */}
        <div className='min-h-full h-full md:min-h-0 md:h-auto flex items-center'>
          <Dialog.Panel
            className={clsx(
              'flex flex-col w-full shadow-xl bg-white',
              variant === 'dialog' ? 'max-w-lg md:rounded-xl' : 'h-screen',
              panelClassName,
            )}
            role='dialog'
          >
            <Dialog.Title
              className={`text-lg font-semibold p-4 md:p-6 flex flex-row justify-between items-center w-full`}
            >
              {/** Used in combination with justify-between to get the title centereD and close button anchored to the right instead of conditionally rendering a grid */}
              {props.variant === 'full-screen' && <div className={'hidden md:inline'}></div>}
              <div>{title}</div>
              {dismissable && (
                <div className='mt-0.5' data-testid='modal-x-button'>
                  <Icon type='x' stroke='gray-300' onClick={onClose} />
                </div>
              )}
            </Dialog.Title>
            {props.headerDivision === 'step' && (
              <ProgressBar valueFormat='percent' value={props.progress} valueLocation='none' />
            )}
            {props.headerDivision === 'divider' && <div className='h-px w-full bg-gray-100'></div>}
            <div
              className={`${variant === 'full-screen' ? 'flex flex-row justify-center overflow-y-auto' : ''} p-6 flex-grow`}
              ref={contentRef}
            >
              {/* Additional spacing to account for the floating footer bar so as to not
              obscure content */}
              <div className={`${ignoreExtraPadding ? '' : 'pb-[108px]'}`}>{children}</div>
            </div>
            {(secondaryCTA || primaryCTA) && (
              <div
                className={cx(
                  'flex flex-row justify-end w-full gap-4 p-4 md:p-8 bg-gray-50',
                  props.variant === 'dialog' ? 'md:bg-white md:rounded-xl' : '',
                  // Allows in-modal popovers like the calendar component to appear above
                  // the footer bar
                  props.variant === 'full-screen' && 'absolute bottom-0',
                )}
              >
                {secondaryCTA && (
                  <Button
                    variant={secondaryCTA.buttonVariant || 'secondary-gray'}
                    size='lg'
                    onPress={secondaryCTA.callback}
                    isDisabled={secondaryCTA.isDisabled?.()}
                  >
                    {secondaryCTA.text}
                  </Button>
                )}
                {primaryCTA && (
                  <Button
                    type='submit'
                    variant={primaryCTA.buttonVariant || 'primary'}
                    size='lg'
                    onPress={() => {
                      if (formRef?.current?.checkValidity()) {
                        primaryCTA.callback();
                      }
                    }}
                    isDisabled={primaryCTA.isDisabled?.() || primaryCTA.isLoading}
                  >
                    {primaryCTA.isLoading && primaryCTA.isLoadingText ?
                      primaryCTA.isLoadingText
                    : primaryCTA.text}
                  </Button>
                )}
              </div>
            )}
          </Dialog.Panel>
        </div>
      </form>
    </Dialog>
  );
};
